// Library logic backed by the Cloudflare API. 4-level model:
// title → creative(대분류) → file(소분류, owns base) → canvas(variant).
// Mirrors the server tree into session.titles and persists every change.

import { titles } from './session.js';
import * as api from './api.js';
import { reconcileOverrides } from './editor.js';

export const DEFAULT_CANVAS_NAME = 'Default Creative';

export function genId(prefix) {
  return `${prefix}_${Date.now().toString(36)}${Math.random().toString(36).slice(2, 8)}`;
}

function stripExt(name) { return String(name || '').replace(/\.html?$/i, ''); }
function uniqueName(existing, base) {
  const set = new Set(existing);
  if (!set.has(base)) return base;
  let i = 1;
  while (set.has(`${base} (${i})`)) i++;
  return `${base} (${i})`;
}

// ===== Hydration =====
export async function initLibrary() {
  titles.length = 0;
  try {
    const tree = await api.fetchLibrary();   // title.creatives[].files[].canvases[]
    tree.forEach((t) => titles.push(t));
  } catch (e) {
    console.warn('[library] could not load from backend:', e.message);
  }
  updateSelectBarSub();
}

// ===== Lookups =====
export function findTitleById(id) { return titles.find((t) => t.id === id) || null; }
export function findCreativeById(id) {
  for (const t of titles) { const c = t.creatives.find((x) => x.id === id); if (c) return c; }
  return null;
}
export function findFileById(id) {
  for (const t of titles) for (const c of t.creatives) {
    const f = (c.files || []).find((x) => x.id === id); if (f) return f;
  }
  return null;
}

// ===== Create / ensure =====
export async function ensureTitle(id, name) {
  if (id) { const t = findTitleById(id); if (t) return t; }
  const nm = (name || '제목 없음').trim() || '제목 없음';
  const existing = titles.find((t) => t.name === nm);
  if (existing) return existing;
  const rec = { id: genId('t'), name: nm, creatives: [] };
  titles.push(rec);
  await api.createTitle({ id: rec.id, name: rec.name });
  return rec;
}

export async function ensureCreative(title, id, name) {
  if (id) { const c = title.creatives.find((x) => x.id === id); if (c) return c; }
  const nm = (name || '대분류').trim() || '대분류';
  const existing = title.creatives.find((c) => c.name === nm);
  if (existing) return existing;
  const rec = { id: genId('c'), titleId: title.id, name: nm, files: [] };
  title.creatives.push(rec);
  await api.createCreative({ id: rec.id, titleId: title.id, name: nm });
  return rec;
}

// A new file (소분류) per upload — display name from the file name, unique within
// the creative ("name", "name (1)", …). Owns the base build (uploaded to R2).
export async function addFile(creative, baseFileName, source) {
  const display = uniqueName((creative.files || []).map((f) => f.name), stripExt(baseFileName));
  const id = genId('f');
  const rec = { id, creativeId: creative.id, name: display, sourceKey: id, baseFileName, canvases: [] };
  creative.files = creative.files || [];
  creative.files.push(rec);
  await api.createFile({ id, creativeId: creative.id, name: display, baseFileName, source });
  return rec;
}

// Reflect a freshly uploaded build into an EXISTING 소분류: replace its base,
// rename it to the new file's name, and reconcile every canvas's saved
// overrides against the new field schema (keep matching, reset changed,
// drop removed). Returns the updated file.
export async function reflectIntoFile(creative, file, baseFileName, source) {
  const others = (creative.files || []).filter((f) => f.id !== file.id).map((f) => f.name);
  const display = uniqueName(others, stripExt(baseFileName));
  file.name = display;
  file.baseFileName = baseFileName;

  await api.replaceFileBase({ id: file.id, name: display, baseFileName, source });

  for (const cv of file.canvases || []) {
    const { pkg, tw } = reconcileOverrides(source, cv.pkg || {}, cv.tw || []);
    const changed = JSON.stringify(pkg) !== JSON.stringify(cv.pkg || {})
                 || JSON.stringify(tw)  !== JSON.stringify(cv.tw  || []);
    if (changed) {
      cv.pkg = pkg; cv.tw = tw;
      await api.updateCanvas(cv.id, { pkg, tw });
    }
  }
  return file;
}

export function nextCanvasName(file, baseName = DEFAULT_CANVAS_NAME) {
  return uniqueName((file.canvases || []).map((c) => c.name), baseName);
}

export async function addCanvas({ file, pkg, tw, name }) {
  const id = genId('cv');
  const rec = { id, fileId: file.id, name, pkg: pkg || {}, tw: tw || [] };
  file.canvases = file.canvases || [];
  file.canvases.push(rec);
  await api.createCanvas({ id, fileId: file.id, name, pkg: rec.pkg, tw: rec.tw });
  return rec;
}

export async function saveCanvasOverrides(cv, pkg, tw) {
  cv.pkg = pkg; cv.tw = tw;
  await api.updateCanvas(cv.id, { pkg, tw });
  return cv;
}

// ===== Rename / delete =====
export async function renameTitle(title, name) { title.name = name; await api.updateTitle(title.id, { name }); }
export async function deleteTitle(title) {
  const i = titles.findIndex((t) => t.id === title.id); if (i >= 0) titles.splice(i, 1);
  await api.deleteTitle(title.id);
}
export async function renameCreative(creative, name) { creative.name = name; await api.updateCreative(creative.id, { name }); }
export async function deleteCreative(title, creative) {
  const i = title.creatives.findIndex((c) => c.id === creative.id); if (i >= 0) title.creatives.splice(i, 1);
  await api.deleteCreative(creative.id);
}
export async function renameFile(file, name) { file.name = name; await api.updateFile(file.id, { name }); }
export async function deleteFile(creative, file) {
  const i = (creative.files || []).findIndex((f) => f.id === file.id); if (i >= 0) creative.files.splice(i, 1);
  await api.deleteFile(file.id);
}
export async function renameCanvas(cv, name) { cv.name = name; await api.updateCanvas(cv.id, { name }); }
export async function deleteCanvas(file, cv) {
  const i = (file.canvases || []).findIndex((c) => c.id === cv.id); if (i >= 0) file.canvases.splice(i, 1);
  await api.deleteCanvasApi(cv.id);
}

// ===== Counts =====
export function updateSelectBarSub() {
  const el = document.getElementById('selectBarSub');
  if (!el) return;
  let creatives = 0, files = 0, canvases = 0;
  for (const t of titles) {
    creatives += t.creatives.length;
    for (const c of t.creatives) {
      files += (c.files || []).length;
      for (const f of (c.files || [])) canvases += (f.canvases || []).length;
    }
  }
  el.textContent = `${titles.length} titles · ${creatives} 대분류 · ${files} 소분류 · ${canvases} canvases`;
}
